import { WEBUI_API_BASE_URL } from '$lib/constants';

const readPayload = async (res: Response) => {
	const contentType = res.headers?.get?.('content-type') ?? '';
	if (contentType.includes('application/json') && typeof res.json === 'function') {
		return await res.json();
	}
	const text = typeof res.text === 'function' ? await res.text() : '';
	return text ? { detail: text } : null;
};

const readErrorPayload = async (res: Response) => {
	try {
		return await readPayload(res);
	} catch {
		return null;
	}
};

export class ContentFactoryBrandVoiceApiError extends Error {
	readonly status: number;
	readonly detail: string;

	constructor(status: number, payload: unknown, statusText = '') {
		super(normalizeBrandVoiceError(payload, statusText));
		this.name = 'ContentFactoryBrandVoiceApiError';
		this.status = status;
		this.detail = this.message;
	}
}

const request = async (token: string, path: string, options: RequestInit = {}) => {
	let error: unknown = null;
	const res = await fetch(`${WEBUI_API_BASE_URL}/content-factory/core${path}`, {
		...options,
		headers: {
			Accept: 'application/json',
			'Content-Type': 'application/json',
			...(token && { authorization: `Bearer ${token}` }),
			...(options.headers ?? {})
		}
	})
		.then(async (res) => {
			if (!res.ok) {
				const payload = await readErrorPayload(res);
				throw new ContentFactoryBrandVoiceApiError(res.status, payload, res.statusText);
			}
			return await readPayload(res);
		})
		.catch((err) => {
			error = err;
			console.error(err);
			return null;
		});

	if (error) throw error;
	return res;
};

export const normalizeBrandVoiceError = (err: unknown, fallback = 'Brand Voice request failed') => {
	if (typeof err === 'string') return err;
	if (err && typeof err === 'object') {
		const value = err as Record<string, any>;
		if (typeof value.detail === 'string') return value.detail;
		if (Array.isArray(value.detail))
			return value.detail.map((item) => item.msg ?? item.message ?? String(item)).join(', ');
		if (typeof value.message === 'string') return value.message;
	}
	return fallback;
};

export type BrandVoiceFindingStatus = 'pending' | 'accepted' | 'edited' | 'rejected' | 'applied';

export type BrandVoiceFinding = {
	id: string;
	run_id: string;
	workspace_id: string;
	brand_id: string;
	category: string;
	field_path?: string | null;
	title: string;
	summary?: string | null;
	content_json: Record<string, any>;
	edited_content_json?: Record<string, any> | null;
	evidence_json?: any;
	confidence?: number | null;
	status: BrandVoiceFindingStatus;
	review_note?: string | null;
};

export type BrandVoiceRun = {
	id: string;
	workspace_id: string;
	brand_id: string;
	status: string;
	platform?: string | null;
	language?: string | null;
	analysis_version: string;
	markdown_report?: string | null;
};

export const analyzeBrandVoicePosts = async (
	token: string,
	brandId: string,
	payload: Record<string, any>
) =>
	await request(token, `/brands/${brandId}/voice-analysis/analyze-posts`, {
		method: 'POST',
		body: JSON.stringify(payload)
	});

export const analyzeBrandVoiceSources = async (
	token: string,
	brandId: string,
	payload: Record<string, any>,
	options: RequestInit = {}
) =>
	await request(token, `/brands/${brandId}/voice-analysis/analyze-sources`, {
		...options,
		method: 'POST',
		body: JSON.stringify(payload)
	});

export const listBrandVoiceRuns = async (
	token: string,
	brandId: string,
	params: Record<string, string | number | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value !== undefined && value !== '') query.set(key, String(value));
	});
	return await request(
		token,
		`/brands/${brandId}/voice-analysis/runs${query.size ? `?${query}` : ''}`,
		{
			method: 'GET'
		}
	);
};

export const getBrandVoiceRun = async (
	token: string,
	brandId: string,
	runId: string,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-analysis/runs/${runId}${query.size ? `?${query}` : ''}`,
		{ method: 'GET' }
	);
};

export const listBrandVoiceFindings = async (
	token: string,
	brandId: string,
	runId: string,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-analysis/runs/${runId}/findings${query.size ? `?${query}` : ''}`,
		{ method: 'GET' }
	);
};

export const reviewBrandVoiceFinding = async (
	token: string,
	brandId: string,
	runId: string,
	findingId: string,
	payload: {
		status: 'accepted' | 'edited' | 'rejected';
		edited_content?: Record<string, any> | null;
		review_note?: string;
	}
) =>
	await request(token, `/brands/${brandId}/voice-analysis/runs/${runId}/findings/${findingId}`, {
		method: 'PATCH',
		body: JSON.stringify(payload)
	});

export const reviewBrandVoiceFindingsBatch = async (
	token: string,
	brandId: string,
	runId: string,
	items: Array<{
		finding_id: string;
		status: 'accepted' | 'edited' | 'rejected';
		edited_content?: Record<string, any> | null;
		review_note?: string;
	}>
) =>
	await request(token, `/brands/${brandId}/voice-analysis/runs/${runId}/findings/review-batch`, {
		method: 'PATCH',
		body: JSON.stringify({ items })
	});

export const applyBrandVoiceRun = async (
	token: string,
	brandId: string,
	runId: string,
	payload: { apply_memory_entries?: boolean } = { apply_memory_entries: true }
) =>
	await request(token, `/brands/${brandId}/voice-analysis/runs/${runId}/apply`, {
		method: 'POST',
		body: JSON.stringify(payload)
	});

export const getBrandVoiceRuntimeContext = async (
	token: string,
	brandId: string,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-runtime/context${query.size ? `?${query}` : ''}`,
		{
			method: 'GET'
		}
	);
};

export const generateWithBrandVoice = async (
	token: string,
	brandId: string,
	payload: Record<string, any>,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-runtime/generate${query.size ? `?${query}` : ''}`,
		{
			method: 'POST',
			body: JSON.stringify(payload)
		}
	);
};

export const validateBrandVoiceText = async (
	token: string,
	brandId: string,
	payload: Record<string, any>,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-runtime/validate${query.size ? `?${query}` : ''}`,
		{
			method: 'POST',
			body: JSON.stringify(payload)
		}
	);
};

export const saveBrandVoiceDraft = async (
	token: string,
	brandId: string,
	payload: Record<string, any>,
	params: Record<string, string | undefined> = {}
) => {
	const query = new URLSearchParams();
	Object.entries(params).forEach(([key, value]) => {
		if (value) query.set(key, value);
	});
	return await request(
		token,
		`/brands/${brandId}/voice-runtime/save-draft${query.size ? `?${query}` : ''}`,
		{
			method: 'POST',
			body: JSON.stringify(payload)
		}
	);
};
